/**
 * Who can join a group call: Invited only, Ask to join (a lobby the host
 * admits from), or Open — and, whatever the mode, people, roles or levels let
 * in directly. The same editor sits in the Staff calls group bar, in the
 * meeting form and in the call window's settings, so a person learns it once.
 *
 * The administrator's rule still stands on every path but "invited": a person
 * the policy would not let call the host cannot come in, however open the
 * call. The editor says so in its note.
 */
import { useState } from "react";
import { useCallDirectory, useCallPolicy } from "../../api/queries";
import {
  JOIN_MODE_LABEL,
  LEVEL_LABEL,
  LEVELS,
  type EndType,
  type JoinMode,
  type JoinRule,
  type Level,
  type PolicyEnd,
} from "../../domain/calls";
import { t } from "../../i18n";
import j from "./JoinRule.module.css";

const MODES: JoinMode[] = ["invited", "approval", "open"];

const MODE_HINT: Record<JoinMode, string> = {
  invited: "Only the people you ring can come in.",
  approval: "Anyone may ask; a host lets them in from the lobby.",
  open: "Anyone on staff calls the rules allow may come straight in.",
};

export function useSubjectLabel() {
  const dir = useCallDirectory();
  const policy = useCallPolicy();
  return (e: PolicyEnd): string => {
    switch (e.type) {
      case "anyone":
        return t("Anyone");
      case "level":
        return t("anyone at {level}", { level: t(LEVEL_LABEL[(e.id as Level) ?? "operations"] ?? e.id ?? "") });
      case "role":
        return t("role: {role}", { role: policy.data?.roles.find((r) => r.key === e.id)?.name ?? e.id ?? "" });
      case "principal":
        return dir.data?.items.find((p) => p.principalId === e.id)?.name ?? e.id ?? "";
    }
  };
}

export function JoinRuleEditor({
  value,
  onChange,
  disabled,
  compact,
  testId = "join",
}: {
  value: JoinRule;
  onChange: (v: JoinRule) => void;
  disabled?: boolean;
  /** one line: the modes and the chips, without the hint */
  compact?: boolean;
  testId?: string;
}) {
  const dir = useCallDirectory();
  const policy = useCallPolicy();
  const label = useSubjectLabel();
  const [type, setType] = useState<Exclude<EndType, "anyone">>("principal");
  const [id, setId] = useState("");
  const people = dir.data?.items ?? [];
  const roles = policy.data?.roles ?? [];
  const has = (e: PolicyEnd) => value.allow.some((a) => a.type === e.type && a.id === e.id);
  const add = () => {
    if (!id) return;
    const e: PolicyEnd = { type, id };
    if (!has(e)) onChange({ ...value, allow: [...value.allow, e] });
    setId("");
  };
  return (
    <div className={j.editor} data-testid={testId}>
      <div className={j.modes} role="radiogroup" aria-label={t("Who can join")}>
        {MODES.map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={value.mode === m}
            className={j.mode}
            disabled={disabled}
            onClick={() => onChange({ ...value, mode: m })}
            data-testid={`${testId}-mode-${m}`}
          >
            {t(JOIN_MODE_LABEL[m])}
          </button>
        ))}
      </div>
      {!compact && <p className={j.hint}>{t(MODE_HINT[value.mode])}</p>}
      <div className={j.allowRow}>
        <span className={j.allowLabel}>{t("Also let in directly:")}</span>
        {value.allow.length === 0 && <span className={j.none}>{t("nobody else")}</span>}
        {value.allow.map((a) => (
          <span key={`${a.type}:${a.id}`} className={j.chip}>
            {label(a)}
            {!disabled && (
              <button
                type="button"
                aria-label={t("Remove {name}", { name: label(a) })}
                onClick={() => onChange({ ...value, allow: value.allow.filter((x) => x !== a) })}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <div className={j.addRow}>
          <select
            className={j.select}
            value={type}
            onChange={(e) => {
              setType(e.target.value as Exclude<EndType, "anyone">);
              setId("");
            }}
            aria-label={t("Kind")}
            data-testid={`${testId}-allow-type`}
          >
            <option value="principal">{t("Person")}</option>
            <option value="role">{t("Role")}</option>
            <option value="level">{t("Level")}</option>
          </select>
          <select
            className={j.select}
            value={id}
            onChange={(e) => setId(e.target.value)}
            aria-label={t("Who")}
            data-testid={`${testId}-allow-id`}
          >
            <option value="">{t("Choose…")}</option>
            {type === "principal" &&
              people.map((p) => (
                <option key={p.principalId} value={p.principalId}>
                  {p.name}
                </option>
              ))}
            {type === "role" &&
              roles.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.name}
                </option>
              ))}
            {type === "level" &&
              LEVELS.map((l) => (
                <option key={l} value={l}>
                  {t(LEVEL_LABEL[l])}
                </option>
              ))}
          </select>
          <button type="button" className={j.add} disabled={!id} onClick={add} data-testid={`${testId}-allow-add`}>
            {t("Add")}
          </button>
        </div>
      )}
      {!compact && (
        <p className={j.hint}>
          {t("The administrator's rule still applies: nobody comes in who may not call the host.")}
        </p>
      )}
    </div>
  );
}
